// Three-Bucket Keyword Distiller — TAB layout
//
// Combines the full master keyword list from all three sources
//   1. Seed keywords the user typed
//   2. Related keywords ("related" concept-group in Google Ads response
//      / DFS related keyword expansions — both live in result.ideas)
//   3. Native Keyword Planner ideas (result.ideas)
// … then de-dupes and asks Claude Sonnet 4.5 to bucket each keyword into:
//   • Target   — commercial / transactional, non-branded
//   • Branded  — contains a rival brand name (auto-expanded)
//   • Negatives — informational / navigational / low-intent
//
// UI: three top tabs with live counts; each tab is a table with
// per-row manual override checkboxes, per-bucket Select-all / Export CSV /
// Confirm-this-bucket. A bottom footer keeps a global "Confirm All" and
// a "Push negatives to Ads" one-click.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Sparkle,
    ArrowsClockwise,
    Target as TargetIcon,
    Prohibit,
    Buildings,
    Check,
    Download,
    UploadSimple,
    Rocket,
    X,
} from "@phosphor-icons/react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { distillKeywords, campaignBuilderPushNegatives, errText } from "@/lib/api";

const fmtNum = (n) => {
    if (n === null || n === undefined) return "—";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
};

const csvDownload = (filename, rows, cols) => {
    if (!rows.length) {
        toast.error("Bucket is empty — nothing to export");
        return;
    }
    const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(",")].concat(
        rows.map((r) => cols.map((c) => esc(r[c])).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

// --- master list: union of seeds + serp-imported + related + planner ideas, then dedupe.
const buildMasterList = ({ ideas, seeds, seedText, serpImported = [] }) => {
    const seen = new Set();
    const out = [];
    // SERP-imported related searches first — they're the most contextually
    // relevant to the user's active research and are cheaply proven to be
    // real Google queries.
    for (const kw of serpImported) {
        const clean = (kw || "").trim();
        if (!clean) continue;
        const norm = clean.toLowerCase().replace(/\s+/g, " ");
        if (seen.has(norm)) continue;
        seen.add(norm);
        out.push({ keyword: clean, source: "serp_related", volume: null });
    }
    // Seeds next — user-typed.
    const seedPool = [
        ...((seeds || []).map((s) => (typeof s === "string" ? s : s?.keyword || ""))),
        ...(seedText || "").split(/[\n,;]+/).map((s) => s.trim()),
    ];
    for (const kw of seedPool) {
        const clean = (kw || "").trim();
        if (!clean) continue;
        const norm = clean.toLowerCase().replace(/\s+/g, " ");
        if (seen.has(norm)) continue;
        seen.add(norm);
        out.push({ keyword: clean, source: "seed", volume: null });
    }
    // Then everything from the Keyword Planner ideas (includes related +
    // planner-generated expansions — both come back inside `result.ideas`).
    for (const i of ideas || []) {
        const kw = (i.keyword || "").trim();
        if (!kw) continue;
        const norm = kw.toLowerCase().replace(/\s+/g, " ");
        if (seen.has(norm)) continue;
        seen.add(norm);
        const grp = i.concepts && i.concepts[0] && i.concepts[0].group;
        const isRelated =
            grp && /related/i.test(String(grp));
        out.push({
            keyword: kw,
            source: isRelated ? "related" : "planner",
            volume: i.avg_monthly_searches ?? null,
        });
    }
    return out;
};

const TAB_META = {
    target: {
        label: "Target",
        sub: "Non-branded · commercial/transactional",
        icon: TargetIcon,
        accent: {
            bar: "bg-emerald-50 border-emerald-400",
            text: "text-emerald-900",
            badgeOn: "bg-emerald-700 text-white",
            badgeOff: "border border-emerald-300 text-emerald-800 bg-white",
            btn: "border border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800",
        },
    },
    branded: {
        label: "Branded",
        sub: "Competitor brand names — bid separately or block",
        icon: Buildings,
        accent: {
            bar: "bg-amber-50 border-amber-400",
            text: "text-amber-900",
            badgeOn: "bg-amber-700 text-white",
            badgeOff: "border border-amber-300 text-amber-800 bg-white",
            btn: "border border-amber-700 bg-amber-700 text-white hover:bg-amber-800",
        },
    },
    negatives: {
        label: "Negatives",
        sub: "Informational / navigational / spend-wasters",
        icon: Prohibit,
        accent: {
            bar: "bg-red-50 border-red-400",
            text: "text-red-900",
            badgeOn: "bg-red-700 text-white",
            badgeOff: "border border-red-300 text-red-800 bg-white",
            btn: "border border-red-700 bg-red-700 text-white hover:bg-red-800",
        },
    },
};

export const KeywordDistillerDialog = ({
    open,
    onOpenChange,
    ideas,
    seeds = [],
    seedText = "",
    serpImported = [],
    initialBrandText = "",
    country = "",
    city = "",
    competitorUrls = [],
    customerId = "",
    onConfirm, // ({ targets, branded, negatives }) => void
    onBuildCampaign, // ({ targets, branded, negatives }) => void
    preloaded = null, // { target, branded, negatives, brand_terms_expanded, summary, created_at, label }
    onAuditSaved,
}) => {
    const [brandText, setBrandText] = useState("");
    const [loading, setLoading] = useState(false);
    const [buckets, setBuckets] = useState(null);
    const [checked, setChecked] = useState({});
    const [activeTab, setActiveTab] = useState("target");
    const [pushingNegs, setPushingNegs] = useState(false);

    useEffect(() => {
        if (!open) return;
        setActiveTab("target");
        setBrandText(initialBrandText || "");
        if (preloaded) {
            // Rehydrate from a persisted Search History run.
            setBuckets({
                target: preloaded.target || [],
                branded: preloaded.branded || [],
                negatives: preloaded.negatives || [],
                brand_terms_expanded: preloaded.brand_terms_expanded || [],
                summary: preloaded.summary || {},
            });
            const c = {};
            (preloaded.target || []).forEach((x) => (c[x.keyword] = true));
            (preloaded.branded || []).forEach((x) => (c[x.keyword] = true));
            (preloaded.negatives || []).forEach((x) => (c[x.keyword] = true));
            setChecked(c);
        } else {
            setBuckets(null);
            setChecked({});
        }
    }, [open, initialBrandText, preloaded]);

    const masterList = useMemo(
        () => buildMasterList({ ideas, seeds, seedText, serpImported }),
        [ideas, seeds, seedText, serpImported]
    );

    // Breakdown for the header summary — proves all sources merge.
    const sourceBreakdown = useMemo(() => {
        const b = { seed: 0, serp_related: 0, related: 0, planner: 0 };
        for (const it of masterList) b[it.source] = (b[it.source] || 0) + 1;
        return b;
    }, [masterList]);

    const brandTerms = useMemo(
        () =>
            brandText
                .split(/[\n,;]/)
                .map((s) => s.trim())
                .filter(Boolean),
        [brandText]
    );

    const runDistiller = async () => {
        if (!masterList.length) {
            toast.error("No keywords available to distill");
            return;
        }
        setLoading(true);
        const t = toast.loading(
            `Claude sorting ${masterList.length} keywords into 3 buckets…`
        );
        try {
            const r = await distillKeywords({
                keywords: masterList,
                brand_terms: brandTerms,
                competitor_urls: competitorUrls,
                country,
                city,
                seed_text: seedText,
            });
            setBuckets(r);
            // Persist the AI-expanded rival brand list so the CampaignPushWizard
            // can use it later to auto-clean the enriched keyword pool.
            try {
                if (typeof window !== "undefined") {
                    const brands = Array.from(
                        new Set([
                            ...(brandTerms || []),
                            ...(r.brand_terms_expanded || []),
                            ...((r.branded || []).map((x) => x.matched_brand).filter(Boolean)),
                        ])
                    );
                    window.localStorage.setItem(
                        "mb_distiller_brands",
                        JSON.stringify(brands)
                    );
                }
            } catch {
                /* ignore */
            }
            const c = {};
            (r.target || []).forEach((x) => (c[x.keyword] = true));
            (r.branded || []).forEach((x) => (c[x.keyword] = true));
            (r.negatives || []).forEach((x) => (c[x.keyword] = true));
            setChecked(c);
            onAuditSaved?.();
            toast.success(
                `Sorted: ${r.summary.target_count} target · ${r.summary.branded_count} branded · ${r.summary.negative_count} negative`,
                { id: t }
            );
        } catch (e) {
            const msg =
                errText(e, "Distill failed");
            toast.error(msg, { id: t });
        } finally {
            setLoading(false);
        }
    };

    const bucketRows = (key) => buckets?.[key] || [];
    const bucketPicked = (key) =>
        bucketRows(key)
            .filter((r) => checked[r.keyword])
            .map((r) => r.keyword);
    const toggleRow = (kw) =>
        setChecked((p) => ({ ...p, [kw]: !p[kw] }));
    const bucketToggleAll = (key, on) => {
        setChecked((prev) => {
            const next = { ...prev };
            for (const r of bucketRows(key)) next[r.keyword] = !!on;
            return next;
        });
    };

    const pickedTarget = bucketPicked("target");
    const pickedBranded = bucketPicked("branded");
    const pickedNegatives = bucketPicked("negatives");

    // Per-bucket independent confirm — feeds only that slice into the
    // Keyword Planner's `selection`. Target/Branded → "target"; Negatives → "negative".
    const confirmBucket = (key) => {
        const picks =
            key === "target"
                ? pickedTarget
                : key === "branded"
                ? pickedBranded
                : pickedNegatives;
        if (!picks.length) {
            toast.error(`No rows ticked in ${TAB_META[key].label}`);
            return;
        }
        const payload = { targets: [], branded: [], negatives: [] };
        payload[key] = picks;
        onConfirm?.(payload);
        toast.success(
            `Applied ${picks.length} ${TAB_META[key].label} row${picks.length === 1 ? "" : "s"} to the Planner`
        );
    };

    const confirmAll = () => {
        if (!buckets) return;
        const total = pickedTarget.length + pickedBranded.length + pickedNegatives.length;
        if (!total) {
            toast.error("Tick some rows first");
            return;
        }
        onConfirm?.({
            targets: pickedTarget,
            branded: pickedBranded,
            negatives: pickedNegatives,
        });
        toast.success(
            `Applied ${pickedTarget.length + pickedBranded.length} bidding · ${pickedNegatives.length} negatives`
        );
        onOpenChange(false);
    };

    const handlePushNegatives = async () => {
        if (!pickedNegatives.length) {
            toast.error("Tick some negatives first");
            return;
        }
        if (!customerId) {
            toast.error("Pick a client account in the Keyword Planner first");
            return;
        }
        setPushingNegs(true);
        const t = toast.loading(
            `Pushing ${pickedNegatives.length} negatives to a shared list…`
        );
        try {
            const data = await campaignBuilderPushNegatives({
                customer_id: customerId,
                list_name: "",
                negatives: pickedNegatives,
            });
            toast.success(
                `Created "${data.list_name}" with ${data.count} negatives`,
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Push failed");
            toast.error(msg, { id: t });
        } finally {
            setPushingNegs(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-6xl w-[94vw] max-h-[92vh] overflow-hidden rounded-none border-2 border-ink p-0 bg-white flex flex-col"
                data-testid="distiller-dialog"
            >
                <DialogHeader className="p-4 border-b-2 border-ink shrink-0">
                    <DialogTitle className="font-display text-xl font-black tracking-tighter text-ink flex items-center gap-2">
                        <Sparkle size={18} weight="bold" className="text-klein" />
                        Three-Bucket Distiller
                    </DialogTitle>
                    <div className="text-[11px] font-mono text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span data-testid="distiller-master-count">
                            {masterList.length} deduped keywords
                        </span>
                        <span className="text-neutral-400">·</span>
                        <span data-testid="distiller-source-breakdown">
                            <span className="text-emerald-700">seed {sourceBreakdown.seed || 0}</span>
                            {" + "}
                            <span className="text-red-700">serp {sourceBreakdown.serp_related || 0}</span>
                            {" + "}
                            <span className="text-klein">related {sourceBreakdown.related || 0}</span>
                            {" + "}
                            <span className="text-ink">planner {sourceBreakdown.planner || 0}</span>
                        </span>
                    </div>
                </DialogHeader>

                {/* Config bar */}
                <div className="px-4 py-3 border-b border-neutral-300 bg-neutral-50 shrink-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                        <div className="md:col-span-2">
                            <label className="mono-label mb-1 block flex items-center gap-1">
                                <Buildings size={11} weight="bold" /> Competitor brands
                                <span className="text-neutral-400 normal-case ml-1 font-normal">
                                    (comma / newline separated — Claude expands variants)
                                </span>
                            </label>
                            <textarea
                                data-testid="distiller-brands"
                                value={brandText}
                                onChange={(e) => setBrandText(e.target.value)}
                                placeholder="Kent, Aquaguard, Blue Star…"
                                className="w-full min-h-[52px] text-sm border border-neutral-300 focus:border-ink focus:outline-none px-2 py-1.5 font-mono"
                            />
                        </div>
                        <div className="flex flex-col gap-2 md:pt-5">
                            <button
                                data-testid="distiller-run"
                                onClick={runDistiller}
                                disabled={loading || !masterList.length}
                                className="brutal-btn border border-klein bg-klein text-white hover:bg-klein/90 font-mono uppercase tracking-widest text-[11px] font-bold px-3 h-10 flex items-center gap-1.5 disabled:opacity-40"
                            >
                                {loading ? (
                                    <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
                                ) : (
                                    <Sparkle size={13} weight="bold" />
                                )}
                                {buckets ? "Re-run Distiller" : "Run Distiller"}
                            </button>
                            {buckets?.brand_terms_expanded?.length > 0 && (
                                <div
                                    data-testid="distiller-brands-expanded"
                                    className="text-[10px] font-mono text-neutral-600 leading-snug"
                                >
                                    <span className="text-neutral-400">Detected:</span>{" "}
                                    {buckets.brand_terms_expanded.join(", ")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                {buckets && (
                    <div className="border-b-2 border-ink bg-white shrink-0 flex items-stretch overflow-x-auto">
                        {["target", "branded", "negatives"].map((key) => {
                            const meta = TAB_META[key];
                            const Icon = meta.icon;
                            const total = (buckets[key] || []).length;
                            const picked = bucketPicked(key).length;
                            const active = activeTab === key;
                            return (
                                <button
                                    key={key}
                                    data-testid={`distiller-tab-${key}`}
                                    onClick={() => setActiveTab(key)}
                                    className={`relative flex items-center gap-2 px-5 py-3 border-r border-ink/20 min-w-[180px] text-left transition-colors ${
                                        active
                                            ? `${meta.accent.bar} border-b-4 ${meta.accent.text}`
                                            : "bg-white text-neutral-600 hover:bg-neutral-50 border-b-4 border-b-transparent"
                                    }`}
                                >
                                    <Icon size={14} weight="bold" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-heading font-bold uppercase tracking-tight text-sm">
                                            {meta.label}
                                        </div>
                                        <div className="text-[9px] font-mono text-neutral-500 truncate">
                                            {meta.sub}
                                        </div>
                                    </div>
                                    <span
                                        data-testid={`distiller-tab-${key}-count`}
                                        className={`px-2 py-0.5 text-[10px] font-mono font-bold ${
                                            active ? meta.accent.badgeOn : meta.accent.badgeOff
                                        }`}
                                    >
                                        {picked}/{total}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto thin-scroll">
                    {!buckets ? (
                        <div className="p-8 text-center text-sm font-mono text-neutral-500">
                            Enter competitor brand names above (optional) and click{" "}
                            <span className="text-ink font-bold">Run Distiller</span>.
                            <br />
                            Claude will bucket every unique keyword into one of three lists
                            for you to review.
                        </div>
                    ) : (
                        <BucketTable
                            tabKey={activeTab}
                            rows={bucketRows(activeTab)}
                            checked={checked}
                            onToggleRow={toggleRow}
                            onSelectAll={(on) => bucketToggleAll(activeTab, on)}
                            onExport={() =>
                                csvDownload(
                                    `distiller-${activeTab}.csv`,
                                    bucketRows(activeTab),
                                    activeTab === "branded"
                                        ? ["keyword", "matched_brand", "volume", "source", "reason"]
                                        : ["keyword", "volume", "source", "reason"]
                                )
                            }
                            onConfirmBucket={() => confirmBucket(activeTab)}
                            pickedCount={bucketPicked(activeTab).length}
                        />
                    )}
                </div>

                {/* Footer */}
                {buckets && (
                    <div className="border-t-2 border-ink bg-ink text-white px-4 py-3 flex items-center flex-wrap gap-3 shrink-0">
                        <div className="text-[11px] font-mono uppercase tracking-widest">
                            <span data-testid="distiller-summary-bidding" className="mr-3">
                                <TargetIcon
                                    size={11}
                                    weight="bold"
                                    className="inline text-emerald-400 mr-1 -mt-0.5"
                                />
                                {pickedTarget.length + pickedBranded.length} bidding ({pickedTarget.length} + {pickedBranded.length})
                            </span>
                            <span data-testid="distiller-summary-neg">
                                <Prohibit
                                    size={11}
                                    weight="bold"
                                    className="inline text-red-400 mr-1 -mt-0.5"
                                />
                                {pickedNegatives.length} negatives
                            </span>
                        </div>
                        <div className="flex-1" />
                        <button
                            data-testid="distiller-push-negatives"
                            onClick={handlePushNegatives}
                            disabled={pushingNegs || !pickedNegatives.length}
                            className="brutal-btn h-8 px-3 border border-red-400 bg-transparent text-red-300 hover:bg-red-500 hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                            title="Push ticked negatives to a shared negative list"
                        >
                            {pushingNegs ? (
                                <ArrowsClockwise size={11} weight="bold" className="animate-spin" />
                            ) : (
                                <UploadSimple size={11} weight="bold" />
                            )}
                            Push neg to Ads
                        </button>
                        <button
                            data-testid="distiller-build-campaign"
                            onClick={() => {
                                const bidding = pickedTarget.length + pickedBranded.length;
                                if (!bidding) {
                                    toast.error("Tick some Target or Branded rows first");
                                    return;
                                }
                                onBuildCampaign?.({
                                    targets: pickedTarget,
                                    branded: pickedBranded,
                                    negatives: pickedNegatives,
                                });
                            }}
                            disabled={!(pickedTarget.length + pickedBranded.length)}
                            className="brutal-btn h-8 px-3 border border-emerald-400 bg-emerald-500 text-white hover:bg-emerald-400 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                            title="Send ticked Target + Branded into the Google Ads Campaign builder"
                        >
                            <Rocket size={11} weight="bold" />
                            Build Google Ads
                        </button>
                        <button
                            data-testid="distiller-confirm-all"
                            onClick={confirmAll}
                            className="brutal-btn h-8 px-4 border border-klein bg-klein text-white hover:bg-klein/90 font-mono uppercase tracking-widest text-[11px] font-bold flex items-center gap-1"
                        >
                            <Check size={12} weight="bold" /> Confirm all &amp; close
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

// ---- Table for the active tab ----
const BucketTable = ({
    tabKey,
    rows,
    checked,
    onToggleRow,
    onSelectAll,
    onExport,
    onConfirmBucket,
    pickedCount,
}) => {
    const meta = TAB_META[tabKey];
    const showBrandCol = tabKey === "branded";
    return (
        <div>
            {/* Action bar */}
            <div className={`px-4 py-2 flex items-center gap-2 border-b ${meta.accent.bar} ${meta.accent.text}`}>
                <button
                    data-testid={`bucket-${tabKey}-select-all`}
                    onClick={() => onSelectAll(true)}
                    className="h-7 px-2 border border-current bg-white/70 hover:bg-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                >
                    <Check size={10} weight="bold" /> Select all
                </button>
                <button
                    data-testid={`bucket-${tabKey}-deselect-all`}
                    onClick={() => onSelectAll(false)}
                    className="h-7 px-2 border border-current bg-white/40 hover:bg-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                >
                    <X size={10} weight="bold" /> Deselect all
                </button>
                <div className="flex-1" />
                <button
                    data-testid={`bucket-${tabKey}-export`}
                    onClick={onExport}
                    disabled={!rows.length}
                    className="h-7 px-2 border border-ink bg-white text-ink hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                >
                    <Download size={10} weight="bold" /> Export CSV
                </button>
                <button
                    data-testid={`bucket-${tabKey}-confirm`}
                    onClick={onConfirmBucket}
                    disabled={!pickedCount}
                    className={`h-7 px-3 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40 ${meta.accent.btn}`}
                    title={`Apply the ${pickedCount} ticked ${meta.label} rows into the Keyword Planner selection`}
                >
                    <Check size={10} weight="bold" /> Confirm {meta.label.toLowerCase()} ({pickedCount})
                </button>
            </div>

            {!rows.length ? (
                <div className="p-6 text-xs font-mono text-neutral-500 text-center">
                    Bucket empty.
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 sticky top-0 z-10">
                        <tr className="text-left border-b-2 border-ink">
                            <th className="px-3 py-2 w-10"></th>
                            <th className="px-3 py-2 mono-label text-neutral-600">Keyword</th>
                            {showBrandCol && (
                                <th className="px-3 py-2 mono-label text-neutral-600 w-32">Brand</th>
                            )}
                            <th className="px-3 py-2 mono-label text-neutral-600 w-24 text-right">Vol</th>
                            <th className="px-3 py-2 mono-label text-neutral-600 w-20">Source</th>
                            <th className="px-3 py-2 mono-label text-neutral-600">Why</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const on = !!checked[r.keyword];
                            return (
                                <tr
                                    key={r.keyword}
                                    data-testid={`bucket-${tabKey}-row-${r.keyword}`}
                                    className={`border-b border-neutral-100 hover:bg-neutral-50 ${on ? "" : "opacity-50"}`}
                                >
                                    <td className="px-3 py-1.5 align-middle">
                                        <input
                                            type="checkbox"
                                            checked={on}
                                            onChange={() => onToggleRow(r.keyword)}
                                            className="accent-klein w-4 h-4"
                                            data-testid={`bucket-${tabKey}-cb-${r.keyword}`}
                                        />
                                    </td>
                                    <td className="px-3 py-1.5 text-ink font-medium">{r.keyword}</td>
                                    {showBrandCol && (
                                        <td className="px-3 py-1.5">
                                            {r.matched_brand ? (
                                                <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                                    {r.matched_brand}
                                                </span>
                                            ) : (
                                                <span className="text-neutral-400 text-[10px]">—</span>
                                            )}
                                        </td>
                                    )}
                                    <td className="px-3 py-1.5 text-right font-mono text-ink">{fmtNum(r.volume)}</td>
                                    <td className="px-3 py-1.5">
                                        <SourceBadge src={r.source} />
                                    </td>
                                    <td className="px-3 py-1.5 text-[11px] font-mono text-neutral-600">
                                        {r.reason || "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const SourceBadge = ({ src }) => {
    const cfg = {
        seed: { cls: "bg-emerald-100 text-emerald-800 border border-emerald-300", label: "SEED" },
        serp_related: { cls: "bg-red-100 text-red-800 border border-red-300", label: "SERP" },
        related: { cls: "bg-klein/10 text-klein border border-klein/30", label: "RELATED" },
        planner: { cls: "bg-neutral-100 text-neutral-700 border border-neutral-300", label: "PLANNER" },
    }[src] || null;
    if (!cfg) return <span className="text-neutral-400 text-[10px]">—</span>;
    return (
        <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold ${cfg.cls}`}>
            {cfg.label}
        </span>
    );
};
